/*
 * «Состав и состояние»: пользователи, проекты, группы, образы в registry, доступность, экспортёры
 * метрик и фоновые задания — мгновенные запросы к Prometheus кластера, который собирает метрики
 * GitLab (первый из viva.company.com/metrics-clusters; компонент gitlab-metrics). Прав
 * администратора GitLab не нужно: счётчики отдаёт gitlab-exporter (gitlab_database_rows).
 */
import { Grid, Typography, makeStyles } from '@material-ui/core';
import { InfoCard, Progress } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import { GITLAB_STAT_QUERIES } from '../../../lib/gitlab';
import { queryInstant } from '../../../lib/prometheus';
import { entityClusters } from '../../../lib/viva';

const useStyles = makeStyles(theme => ({
  tile: { display: 'flex', flexDirection: 'column', gap: 2 },
  number: { fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  label: { fontSize: 12, color: theme.palette.text.secondary },
  bad: { color: theme.palette.status.error },
  warn: { color: theme.palette.status.warning },
  muted: { color: theme.palette.text.secondary },
}));

type Stats = Partial<Record<keyof typeof GITLAB_STAT_QUERIES, number>>;

const Tile = ({ label, value, tone }: { label: string; value: string; tone?: 'bad' | 'warn' }) => {
  const classes = useStyles();
  return (
    <Grid item xs={6} sm={4} md={3}>
      <div className={classes.tile}>
        <span className={`${classes.number} ${tone ? classes[tone] : ''}`}>{value}</span>
        <span className={classes.label}>{label}</span>
      </div>
    </Grid>
  );
};

const count = (v?: number) => (v === undefined || Number.isNaN(v) ? '—' : String(Math.round(v)));

export const GitlabStatsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const cluster = entityClusters(entity)[0]?.cluster;
  const { value, loading, error } = useAsync(async (): Promise<Stats> => {
    if (!cluster) return {};
    const entries = await Promise.all(
      Object.entries(GITLAB_STAT_QUERIES).map(async ([key, query]) => {
        const samples = await queryInstant({ discoveryApi, fetchApi }, cluster, query);
        return [key, samples[0]?.value] as const;
      }),
    );
    return Object.fromEntries(entries);
  }, [cluster]);

  const title = 'Состав и состояние';
  if (!cluster) {
    return (
      <InfoCard title={title}>
        <Typography className={classes.muted}>
          Метрики GitLab не собирает ни один кластер. Включите «Метрики GitLab платформы» при
          создании кластера (обычно в одном, dev).
        </Typography>
      </InfoCard>
    );
  }
  const s = value ?? {};
  const up = s.exportersUp ?? 0;
  const total = s.exporters ?? 0;
  let available = '—';
  if (s.available === 1) available = 'да';
  else if (s.available === 0) available = 'нет';
  const latency = s.queueLatency;
  return (
    <InfoCard title={title} subheader={`по метрикам кластера ${cluster}`}>
      {loading && <Progress />}
      {error && <Typography className={classes.muted}>Prometheus {cluster}: {error.message}</Typography>}
      <Grid container spacing={2}>
        <Tile label="активных пользователей" value={count(s.users)} />
        <Tile label="проектов" value={count(s.projects)} />
        <Tile label="групп" value={count(s.groups)} />
        <Tile label="образов в registry" value={count(s.images)} />
        <Tile label="доступен снаружи" value={available} tone={s.available === 0 ? 'bad' : undefined} />
        <Tile
          label="экспортёров метрик отвечает"
          value={total ? `${up} из ${total}` : '—'}
          tone={total && up < total ? 'warn' : undefined}
        />
        <Tile
          label="мёртвых заданий Sidekiq"
          value={count(s.deadJobs)}
          tone={s.deadJobs ? 'warn' : undefined}
        />
        <Tile
          label="задержка очередей Sidekiq, с"
          value={latency === undefined ? '—' : latency.toFixed(1)}
          tone={latency !== undefined && latency > 300 ? 'warn' : undefined}
        />
      </Grid>
    </InfoCard>
  );
};
